/**
 * batch-65 factuur-richting (FO-486) - de pure richting-beoordeling van een
 * binnengekomen factuur: is STREEP de afzender (verkoop), de ontvanger (inkoop)
 * of allebei (intern, FO-432)?
 *
 * Achtergrond: op verkoopfactuur@ komen via de Finqle-mailstroom ook facturen
 * AAN STREEP binnen (Finqle-kosten), en op inkoop@ soms onze eigen
 * verkoopfacturen. De AI-extractie geeft een `factuurRichting`, maar die is
 * een gok van het model; deze functie legt er de harde herkenning naast
 * (KvK, naam, alias, rekeningnummer via `herkenEigenBv`/`herkenEigenBvOpIban`)
 * en geeft een oordeel met zekerheid. Het oordeel stuurt alleen VOORSORTEREN
 * (markering + filter); verplaatsen gebeurt nooit zonder bevestiging, tenzij
 * een beheerder dat per postvak bewust aanzet.
 *
 * Gedeeld door de verkoop- en de inkoop-mailimport (api) en de tests; geen
 * database, geen AI.
 */

import type {
  BoekAlsInkoopBeschikbaarheidDTO,
  BoekAlsVerkoopBeschikbaarheidDTO,
  FactuurRichtingSamenvattingDTO,
} from '@streep/types'
import {
  herkenEigenBv,
  herkenEigenBvOpIban,
  normaliseerKvk,
  normalizeBedrijfsnaam,
  type EigenBvKandidaat,
} from './eigen-bv-herkenning'

export type FactuurRichting = 'VERKOOP' | 'INKOOP' | 'INTERN'
export type RichtingZekerheid = 'HOOG' | 'MIDDEL' | 'LAAG'

/**
 * Waarop een eigen BV herkend is:
 *   - KVK / NAAM: strikte herkenning (`herkenEigenBv`);
 *   - IBAN: het rekeningnummer van deze partij is dat van een eigen BV;
 *   - IBAN_TEGENPARTIJ: het enige rekeningnummer op de factuur is van een eigen
 *     BV, maar de afzender heet anders - dan hoort het nummer bij de ontvanger
 *     (Finqle-facturen dragen vaak alleen het nummer van de tegenpartij);
 *   - AI_BV: alleen de door de AI aangewezen STREEP-BV-naam (zwakst).
 */
export type EigenBvHerkenningsBron = 'KVK' | 'NAAM' | 'IBAN' | 'IBAN_TEGENPARTIJ' | 'AI_BV'

export interface FactuurPartijVelden {
  naam: string | null
  kvk: string | null
  iban: string | null
}

export interface FactuurRichtingVelden {
  /** De uitgever van de factuur (crediteur / leverancier). */
  afzender: FactuurPartijVelden
  /** De geadresseerde (debiteur / "gericht aan"). */
  ontvanger: FactuurPartijVelden
  /** Richting volgens de AI-extractie; alleen de verkoop-extractie kent die. */
  aiRichting?: 'VERKOOP' | 'INKOOP' | null
  /** STREEP-BV-naam volgens de AI (afzender bij verkoop, ontvanger bij inkoop). */
  bvNaam?: string | null
  /**
   * De afzender is aantoonbaar een externe partij (bv. herkend op een
   * ingestelde Finqle-afzenderkenmerk). Maakt de tegenpartij-lezing van een
   * eigen rekeningnummer zeker.
   */
  afzenderExternBevestigd?: boolean
}

export interface HerkendePartij {
  entityId: string
  naam: string
  bron: EigenBvHerkenningsBron
}

export interface FactuurRichtingOordeel {
  richting: FactuurRichting | null
  zekerheid: RichtingZekerheid
  afzender: HerkendePartij | null
  ontvanger: HerkendePartij | null
  /** Korte uitleg voor de reviewer (alleen eigen BV-namen, geen factuurinhoud). */
  reden: string
}

const STRIKT: readonly EigenBvHerkenningsBron[] = ['KVK', 'NAAM', 'IBAN']

function bronVanStrikt(kvk: string | null, e: EigenBvKandidaat): EigenBvHerkenningsBron {
  const k = normaliseerKvk(kvk)
  return k !== null && normaliseerKvk(e.kvkNumber) === k ? 'KVK' : 'NAAM'
}

function partij(e: EigenBvKandidaat, bron: EigenBvHerkenningsBron): HerkendePartij {
  return { entityId: e.id, naam: e.name, bron }
}

function heeftBruikbareNaam(naam: string | null): boolean {
  return naam !== null && normalizeBedrijfsnaam(naam).length >= 3
}

const BRON_UITLEG: Record<EigenBvHerkenningsBron, string> = {
  KVK: 'KvK-nummer',
  NAAM: 'naam',
  IBAN: 'rekeningnummer',
  IBAN_TEGENPARTIJ: 'rekeningnummer van de tegenpartij',
  AI_BV: 'BV-naam volgens de AI',
}

/**
 * Beoordeel de richting van een factuur op grond van de herkende eigen BV's.
 *
 * Regels (in deze volgorde):
 *   1. afzender én ontvanger een eigen BV: INTERN (FO-432 wint), of - bij
 *      dezelfde BV - geen richting (een BV factureert zichzelf niet);
 *   2. alleen de ontvanger: INKOOP; alleen de afzender: VERKOOP;
 *   3. niets herkend: de AI-richting met LAGE zekerheid (of geen richting).
 *
 * Zekerheid HOOG vraagt een strikte herkenning (KvK, naam of het eigen
 * rekeningnummer van die partij) en geen tegenspraak van de AI. Een
 * tegenpartij-rekeningnummer is alleen HOOG als de AI dezelfde kant op wijst of
 * de afzender aantoonbaar extern is; anders MIDDEL. Alleen HOOG mag voorsorteren.
 */
export function beoordeelFactuurRichting<T extends EigenBvKandidaat>(
  velden: FactuurRichtingVelden,
  entities: readonly T[],
): FactuurRichtingOordeel {
  const ai = velden.aiRichting ?? null
  // De door de AI aangewezen STREEP-BV-naam (zwakste bron, zie onder).
  const bvOpNaam = velden.bvNaam ? herkenEigenBv(velden.bvNaam, null, entities) : null

  // ── Afzender ──────────────────────────────────────────────────────────────
  let afzender: HerkendePartij | null = null
  let tegenpartijViaIban: HerkendePartij | null = null
  const afzenderStrikt = herkenEigenBv(velden.afzender.naam, velden.afzender.kvk, entities)
  if (afzenderStrikt) {
    afzender = partij(afzenderStrikt, bronVanStrikt(velden.afzender.kvk, afzenderStrikt))
  } else {
    const opIban = herkenEigenBvOpIban(velden.afzender.iban, entities)
    if (opIban) {
      // Een eigen rekeningnummer bij een afzender met een ÁNDERE naam: het
      // nummer hoort dan bij de tegenpartij (de ontvanger), niet bij de afzender.
      // Behalve als de AI zegt dat dit een verkoopfactuur van precies die BV is:
      // dan is de "andere naam" gewoon onze merknaam zonder KvK en is het nummer
      // van de afzender zelf (review I5 - anders een valse inkoop).
      // Is de uitgever aantoonbaar extern (Finqle-kenmerk op naam/e-mail), dan
      // wint dat: dan is het een AI-misser en blijft het de tegenpartij.
      const aiNoemtDezeBvAlsAfzender =
        ai === 'VERKOOP' && bvOpNaam?.id === opIban.id && velden.afzenderExternBevestigd !== true
      if (heeftBruikbareNaam(velden.afzender.naam) && !aiNoemtDezeBvAlsAfzender) {
        tegenpartijViaIban = partij(opIban, 'IBAN_TEGENPARTIJ')
      } else {
        afzender = partij(opIban, 'IBAN')
      }
    }
  }

  // ── Ontvanger ─────────────────────────────────────────────────────────────
  let ontvanger: HerkendePartij | null = null
  // Korte namen ("Service") staan aan de ontvangerkant uit: een externe
  // debiteur "Service B.V." mag nooit als eigen BV gelden (zie intercompany).
  const ontvangerStrikt = herkenEigenBv(velden.ontvanger.naam, velden.ontvanger.kvk, entities, {
    korteNaam: false,
  })
  if (ontvangerStrikt) {
    ontvanger = partij(ontvangerStrikt, bronVanStrikt(velden.ontvanger.kvk, ontvangerStrikt))
  } else {
    const opIban = herkenEigenBvOpIban(velden.ontvanger.iban, entities)
    if (opIban) ontvanger = partij(opIban, 'IBAN')
    else if (tegenpartijViaIban) ontvanger = tegenpartijViaIban
  }

  // ── Zwakste bron: de door de AI aangewezen STREEP-BV-naam ─────────────────
  if (bvOpNaam) {
    if (ai === 'INKOOP' && !ontvanger && afzender?.entityId !== bvOpNaam.id) {
      ontvanger = partij(bvOpNaam, 'AI_BV')
    } else if (ai === 'VERKOOP' && !afzender && ontvanger?.entityId !== bvOpNaam.id) {
      afzender = partij(bvOpNaam, 'AI_BV')
    }
  }

  // ── Oordeel ───────────────────────────────────────────────────────────────
  if (afzender && ontvanger) {
    if (afzender.entityId === ontvanger.entityId) {
      return {
        richting: null,
        zekerheid: 'LAAG',
        afzender,
        ontvanger,
        reden: `Afzender en ontvanger zijn allebei ${afzender.naam}; een BV factureert zichzelf niet.`,
      }
    }
    const beideStrikt = STRIKT.includes(afzender.bron) && STRIKT.includes(ontvanger.bron)
    return {
      richting: 'INTERN',
      zekerheid: beideStrikt ? 'HOOG' : 'MIDDEL',
      afzender,
      ontvanger,
      reden: `Factuur tussen eigen BV's: ${afzender.naam} -> ${ontvanger.naam}.`,
    }
  }

  if (ontvanger) {
    return {
      richting: 'INKOOP',
      zekerheid: zekerheidVoorEenKant(ontvanger.bron, ai, 'INKOOP', velden.afzenderExternBevestigd === true),
      afzender: null,
      ontvanger,
      reden: `${ontvanger.naam} is de ontvanger (herkend op ${BRON_UITLEG[ontvanger.bron]}) en de afzender is geen eigen BV.`,
    }
  }

  if (afzender) {
    return {
      richting: 'VERKOOP',
      zekerheid: zekerheidVoorEenKant(afzender.bron, ai, 'VERKOOP', false),
      afzender,
      ontvanger: null,
      reden: `${afzender.naam} is de afzender (herkend op ${BRON_UITLEG[afzender.bron]}) en de ontvanger is geen eigen BV.`,
    }
  }

  return {
    richting: ai,
    zekerheid: 'LAAG',
    afzender: null,
    ontvanger: null,
    reden:
      ai === null
        ? 'Geen eigen BV herkend als afzender of ontvanger.'
        : `Geen eigen BV herkend; alleen de AI schat de richting in als ${ai === 'INKOOP' ? 'inkoop' : 'verkoop'}.`,
  }
}

function zekerheidVoorEenKant(
  bron: EigenBvHerkenningsBron,
  ai: 'VERKOOP' | 'INKOOP' | null,
  richting: 'VERKOOP' | 'INKOOP',
  externBevestigd: boolean,
): RichtingZekerheid {
  const aiTegen = ai !== null && ai !== richting
  if (bron === 'AI_BV') return 'MIDDEL'
  if (bron === 'IBAN_TEGENPARTIJ') {
    if (aiTegen) return 'MIDDEL'
    return ai === richting || externBevestigd ? 'HOOG' : 'MIDDEL'
  }
  return aiTegen ? 'MIDDEL' : 'HOOG'
}

/** Voorsorteren mag alleen op een HOGE zekerheid (spec §6, nooit gokken). */
export function isVermoedelijkInkoop(oordeel: FactuurRichtingOordeel): boolean {
  return oordeel.richting === 'INKOOP' && oordeel.zekerheid === 'HOOG'
}

/** Spiegel voor de inkoop-kant: STREEP is met hoge zekerheid de afzender. */
export function isVermoedelijkVerkoop(oordeel: FactuurRichtingOordeel): boolean {
  return oordeel.richting === 'VERKOOP' && oordeel.zekerheid === 'HOOG'
}

// ─── Finqle-herkenning ───────────────────────────────────────────────────────

/** Een kenmerk moet minstens zo lang zijn; "fi" zou alles matchen. */
export const FINQLE_KENMERK_MIN_LENGTE = 3

/**
 * Normaliseer de ingestelde Finqle-afzenderkenmerken (domein, adres of naam):
 * trim, kleine letters, dubbelen en te korte waarden eruit.
 */
export function normaliseerFinqleKenmerken(kenmerken: readonly string[]): string[] {
  const uit: string[] = []
  for (const k of kenmerken) {
    const n = k.trim().toLowerCase()
    if (n.length >= FINQLE_KENMERK_MIN_LENGTE && !uit.includes(n)) uit.push(n)
  }
  return uit
}

export interface FinqleBronnen {
  /** Afzender van de mail ("Naam <adres>" of kaal adres). */
  mailAfzender?: string | null
  /** Naam van de uitgever volgens de extractie. */
  afzenderNaam?: string | null
  /** E-mailadres van de uitgever volgens de extractie. */
  afzenderEmail?: string | null
}

/**
 * Welk ingesteld kenmerk wijst deze factuur aan als "van Finqle"? Een kenmerk
 * matcht als het (hoofdletterongevoelig) voorkomt in het mailadres, de naam of
 * het e-mailadres van de uitgever. Geen kenmerken ingesteld = nooit Finqle
 * (niets hardcoded; de operator vult de lijst).
 *
 * @returns het treffende kenmerk, of null.
 */
export function herkenFinqleAfzender(
  kenmerken: readonly string[],
  bronnen: FinqleBronnen,
): string | null {
  const velden = [bronnen.mailAfzender, bronnen.afzenderNaam, bronnen.afzenderEmail]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.toLowerCase())
  if (velden.length === 0) return null
  for (const kenmerk of normaliseerFinqleKenmerken(kenmerken)) {
    if (velden.some((v) => v.includes(kenmerk))) return kenmerk
  }
  return null
}

/**
 * Een Finqle-kostenfactuur: de uitgever is Finqle (ingesteld kenmerk) én een
 * eigen BV is de ontvanger, terwijl de afzender zelf géén eigen BV is. Een
 * eigen BV die via de Finqle-merchant factureert blijft gewoon verkoop.
 *
 * Twee zwakke signalen samen zijn geen bewijs (review I5): via Finqle komen ook
 * onze EIGEN verkoopfacturen binnen, dus een kenmerk op alleen het mailadres
 * telt pas als de ontvanger stevig herkend is (KvK, naam of zijn eigen
 * rekeningnummer). Is de ontvanger alleen via het tegenpartij-rekeningnummer of
 * de AI-BV-naam gevonden, dan moet het kenmerk op de uitgever zelf staan.
 */
export function isFinqleKostenfactuur(
  finqleKenmerk: string | null,
  oordeel: FactuurRichtingOordeel,
  opties: { kenmerkOpUitgever?: boolean } = {},
): boolean {
  if (finqleKenmerk === null || oordeel.ontvanger === null || oordeel.afzender !== null) return false
  const zwakkeOntvanger =
    oordeel.ontvanger.bron === 'IBAN_TEGENPARTIJ' || oordeel.ontvanger.bron === 'AI_BV'
  return !zwakkeOntvanger || opties.kenmerkOpUitgever === true
}

// ─── Kostensoortvoorstel ─────────────────────────────────────────────────────

export type KostensoortVoorstelBron = 'LEVERANCIER_DEFAULT' | 'FINQLE' | 'GEEN'

export interface KostensoortVoorstel {
  /** Id uit de kostensoorten-stamtabel (FO-482), of null = geen voorstel. */
  kostensoortId: string | null
  bron: KostensoortVoorstelBron
  /** Uitleg voor de dialoog (waarom deze kostensoort voorgevuld staat). */
  toelichting: string
}

/**
 * Het kostensoortvoorstel bij "Boeken als inkoopfactuur": de leveranciers-
 * default (`Client.defaultKostensoortId`, bron 4 van de kostensoort-cascade)
 * wint; anders krijgt een Finqle-kostenfactuur de stamtabel-soort
 * "Administratie en financieel › Finqle / financieringsplatform"
 * (`FINQLE_KOSTENSOORT_CODE`). De aanroeper geeft de id's mee, omdat die uit de
 * database komen (actieve soort, na een eventuele samenvoeging de opvolger);
 * een id van null betekent: die soort bestaat niet (meer) actief.
 */
export function stelKostensoortVoor(input: {
  leverancierDefaultId: string | null
  finqleKostenfactuur: boolean
  finqleKostensoortId: string | null
}): KostensoortVoorstel {
  if (input.leverancierDefaultId) {
    return {
      kostensoortId: input.leverancierDefaultId,
      bron: 'LEVERANCIER_DEFAULT',
      toelichting: 'Standaard-kostensoort van deze leverancier.',
    }
  }
  if (input.finqleKostenfactuur && input.finqleKostensoortId) {
    return {
      kostensoortId: input.finqleKostensoortId,
      bron: 'FINQLE',
      toelichting: `Finqle-kostenfactuur: ${FINQLE_KOSTENSOORT_OMSCHRIJVING}.`,
    }
  }
  if (input.finqleKostenfactuur) {
    return {
      kostensoortId: null,
      bron: 'GEEN',
      toelichting: `Finqle-kostenfactuur, maar de kostensoort ${FINQLE_KOSTENSOORT_OMSCHRIJVING} is niet actief in de stamtabel; kies de kostensoort bij de controle.`,
    }
  }
  return { kostensoortId: null, bron: 'GEEN', toelichting: 'Geen voorstel; kies de kostensoort bij de controle.' }
}

/**
 * De stamtabel-code (FO-482) van de soort voor Finqle-kosten. Eén plek; de
 * seed (`packages/types/src/kostensoorten-seed.ts`) bevat hem (test borgt dat).
 */
export const FINQLE_KOSTENSOORT_CODE = 'ADM_FINQLE'

/** Hoe de Finqle-soort in markeringen en uitleg heet (hoofdgroep › soort). */
export const FINQLE_KOSTENSOORT_OMSCHRIJVING = 'Administratie en financieel › Finqle / financieringsplatform'

// ─── Beschikbaarheid van de verplaats-knoppen (spec §6) ──────────────────────
// Eén definitie voor de knop (backoffice), de DTO en de route/service (api),
// zodat UI en API nooit uit elkaar lopen. De rechten (FACTUREN.canEdit én
// INKOOP.canEdit, geen ACCOUNTANT) bepaalt de caller en geeft hij mee.

export const UITLEG_GEEN_RECHT =
  'Verplaatsen tussen verkoop en inkoop vraagt bewerkrechten op zowel Facturen als Inkoop.'

/** De velden van een verkoopconcept die de beschikbaarheid bepalen. */
export interface VerkoopConceptStand {
  status: string
  invoiceId: string | null
  /**
   * De geboekte Invoice is intussen verwijderd (FO-443). Dan telt het concept
   * niet meer als geboekt en kan het alsnog als inkoop (review I3); precies wat
   * de AL_GEBOEKT-uitleg belooft.
   */
  invoiceVerwijderd?: boolean
  /**
   * Nummer van een LEVENDE verkoopfactuur met dezelfde PDF (een herstelde
   * factuur, of samengevoegd in een winnaar), of null. Dan is het document al
   * als verkoop geboekt en zou verplaatsen dubbel boeken (review-3 B1). De API
   * vult dit altijd; lijst-rijen laten het weg (de dialoog toetst opnieuw).
   */
  zelfdePdfGeboektAls?: string | null
  isIntercompany: boolean
  deletedAt: Date | null
  richting: FactuurRichtingSamenvattingDTO | null
}

/**
 * "Boeken als inkoopfactuur" (verkoop -> inkoop). Volgorde: eerst de stand van
 * het concept (al geboekt / al verplaatst / weg), dan het intern-pad (FO-432
 * wint), dan de rechten. Ook een eerder AFGEWEZEN concept mag alsnog naar
 * inkoop (vóór FO-486 was afwijzen de enige uitweg voor een inkoopfactuur).
 */
export function beschikbaarheidBoekAlsInkoop(
  c: VerkoopConceptStand,
  magVerplaatsen: boolean,
): BoekAlsInkoopBeschikbaarheidDTO {
  if (c.deletedAt !== null) {
    return { modus: 'NIET_OPEN', uitleg: 'Dit concept is verwijderd.' }
  }
  if (c.status === 'VERPLAATST_NAAR_INKOOP') {
    return { modus: 'AL_VERPLAATST', uitleg: 'Deze factuur is al als inkoopfactuur geboekt.' }
  }
  const invoiceVerwijderd = c.invoiceVerwijderd === true
  if ((c.status === 'GOEDGEKEURD' || c.invoiceId !== null) && !invoiceVerwijderd) {
    return {
      modus: 'AL_GEBOEKT',
      uitleg:
        'Deze factuur is al als verkoopfactuur geboekt. Verplaatsen kan pas als de geboekte factuur weg is: verwijder die eerst (Facturen, factuur openen, Verwijderen); daarna kun je hem hier alsnog als inkoopfactuur boeken.',
    }
  }
  if (c.zelfdePdfGeboektAls) {
    return {
      modus: 'AL_GEBOEKT',
      uitleg: `Deze PDF staat al als verkoopfactuur ${c.zelfdePdfGeboektAls} in de boeken. Verplaatsen naar inkoop zou hetzelfde document twee keer boeken.`,
    }
  }
  const open =
    c.status === 'TE_CONTROLEREN' ||
    c.status === 'AFGEWEZEN' ||
    (c.status === 'GOEDGEKEURD' && invoiceVerwijderd)
  if (!open) {
    return { modus: 'NIET_OPEN', uitleg: 'Dit concept kan niet meer verplaatst worden.' }
  }
  if (c.isIntercompany || c.richting?.richting === 'INTERN') {
    return {
      modus: 'INTERN',
      uitleg:
        "Afzender en ontvanger zijn allebei een eigen BV: dit is een interne factuur (FO-432). Boek hem via Goedkeuren met de intercompany-markering; de ontvangende BV krijgt hem via het interne pad.",
    }
  }
  if (!magVerplaatsen) return { modus: 'GEEN_RECHT', uitleg: UITLEG_GEEN_RECHT }
  return { modus: 'BESCHIKBAAR', uitleg: null }
}

/** De velden van een inkoopfactuur die de beschikbaarheid bepalen. */
export interface InkoopFactuurStand {
  status: string
  teControleren: boolean
  isIntercompany: boolean
  herhaalRegelId: string | null
  deletedAt: Date | null
  /**
   * Nummer van een nog open (CONCEPT) betaallijst waar de factuur op staat, of
   * null. De SEPA-run betaalt uit de snapshot van die lijst, dus eerst eraf.
   */
  openBetaallijstNummer?: string | null
  /**
   * batch-65 integratie (FO-486 x FO-483) - aantal ACTIEVE doorbelastingen van
   * deze factuur naar een verkoopfactuur of werkbon. Zolang die er zijn is de
   * factuur een kost die al bij een klant in rekening wordt gebracht; hem naar
   * verkoop verplaatsen zou die klant laten betalen voor een STREEP-verkoopfactuur.
   */
  actieveDoorbelastingen?: number
}

/** Statussen waarin een concept-inkoopfactuur nog als verkoop geboekt mag worden. */
export const VERPLAATSBARE_INKOOP_STATUSSEN = ['ONTVANGEN', 'IN_BEHANDELING'] as const

/** "Boeken als verkoopfactuur" (inkoop -> verkoop): alleen een concept ter controle. */
export function beschikbaarheidBoekAlsVerkoop(
  f: InkoopFactuurStand,
  magVerplaatsen: boolean,
): BoekAlsVerkoopBeschikbaarheidDTO {
  if (f.status === 'VERPLAATST_NAAR_VERKOOP') {
    return { modus: 'AL_VERPLAATST', uitleg: 'Deze factuur is al als verkoopfactuur doorgezet.' }
  }
  if (f.isIntercompany || f.status === 'KLAARGEZET' || f.herhaalRegelId !== null) {
    return {
      modus: 'INTERN',
      uitleg:
        "Dit is een factuur tussen eigen BV's (FO-432): die volgt het interne pad en wordt niet als verkoopfactuur doorgezet.",
    }
  }
  if (
    f.deletedAt !== null ||
    !f.teControleren ||
    !(VERPLAATSBARE_INKOOP_STATUSSEN as readonly string[]).includes(f.status)
  ) {
    return {
      modus: 'NIET_TE_CONTROLEREN',
      uitleg:
        'Alleen een concept-inkoopfactuur die nog ter controle staat kan als verkoopfactuur geboekt worden.',
    }
  }
  if (f.openBetaallijstNummer) {
    return {
      modus: 'OP_BETAALLIJST',
      uitleg: `Deze factuur staat op betaallijst ${f.openBetaallijstNummer}, die nog niet verwerkt is; de SEPA-run zou hem anders alsnog betalen. Annuleer die betaallijst eerst (Inkoop, Betaallijsten) en maak hem zo nodig opnieuw zonder deze factuur.`,
    }
  }
  if ((f.actieveDoorbelastingen ?? 0) > 0) {
    const n = f.actieveDoorbelastingen ?? 0
    return {
      modus: 'DOORBELAST',
      uitleg: `Deze factuur is doorbelast aan een klant (${n} ${n === 1 ? 'doorbelasting' : 'doorbelastingen'}). Ontkoppel die eerst (Inkoop, deze factuur, Doorbelasten); anders betaalt de klant voor een kost die eigenlijk een verkoopfactuur is.`,
    }
  }
  if (!magVerplaatsen) return { modus: 'GEEN_RECHT', uitleg: UITLEG_GEEN_RECHT }
  return { modus: 'BESCHIKBAAR', uitleg: null }
}
